import React, { useContext } from "react";
import { API, FetchAPIFunc, Error, OrganizationMemberRole } from "../types/apiv1";

const STATUS_BAD_REQUEST = 400;
const STATUS_UNAUTHORIZED = 401;
const STATUS_FORBIDDEN = 403;
const STATUS_NOT_FOUND = 404;
const STATUS_CONFLICT = 409;
const STATUS_INTERNAL_SERVER_ERROR = 500;

// encodeHSegment is like encodeURIComponent but less strict. It allows ";",
// ":", "@", "&", and "=" to remain unencoded.
//
// The goal of this function is to make sure that segments like "@me" aren't
// encoded, as the api does not respond to "%40me".
//
// RFC 1738:
//     httpurl        = "http://" hostport [ "/" hpath [ "?" search ]]
//     hpath          = hsegment *[ "/" hsegment ]
//     hsegment       = *[ uchar | ";" | ":" | "@" | "&" | "=" ]
//     search         = *[ uchar | ";" | ":" | "@" | "&" | "=" ]
const encodeHSegment = (hsegment: string) =>
	encodeURIComponent(hsegment)
		.replace(/%(3B|3A|40|26|3D)/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));

const hpath = (...hsegments: string[]) =>
	hsegments.reduce((hpath, hsegment) => hpath + "/" + encodeHSegment(hsegment), "");

const endpoint = (...hsegments: string[]) => hpath("v1", ...hsegments);

const authEndpoint = (...hsegments: string[]) => hpath("auth", "v1", ...hsegments);

const bodyless = (r: Response) => Promise.resolve(null);

const json = (r: Response) => r.json();

const checkStatus = async (r: Response) => {
	if (r.ok) {
		return r;
	}
	
	switch (r.status) {
	case STATUS_BAD_REQUEST:
		throw Error.BadRequest;
	case STATUS_UNAUTHORIZED:
		throw Error.Unauthorized;
	case STATUS_FORBIDDEN:
		throw Error.Forbidden;
	case STATUS_NOT_FOUND:
		throw Error.NotFound;
	case STATUS_CONFLICT:
		throw Error.Conflict;
	case STATUS_INTERNAL_SERVER_ERROR:
	default:
		throw Error.Bug;
	}
}

const networkError = () => Promise.reject(Error.Network);

type UnauthorizedHandler = () => void;

function autoRefreshingFetch(fetch: FetchAPIFunc, unauthorizedHandlers: UnauthorizedHandler[]): FetchAPIFunc {
	return async (endpoint, init) => {
		const res = await fetch(endpoint, init);

		if (res.status === STATUS_UNAUTHORIZED) {
			const refreshRes = await fetch(authEndpoint("refresh"), { method: "POST" });
			if (refreshRes.ok) {
				return fetch(endpoint, init);
			} else {
				unauthorizedHandlers.forEach(handler => handler());
				return res;
			}
		}

		return res;
	}
}

function createNetwork(fetchFunc: FetchAPIFunc, unauthorizedHandlers: UnauthorizedHandler[]) {
	const fetch = autoRefreshingFetch(fetchFunc, unauthorizedHandlers);
	
	return {
		get(endpoint: string) {
			return fetch(endpoint, {
				method: "GET",
			}).catch(networkError).then(checkStatus);
		},
		post(endpoint: string, body?: object) {
			return fetch(endpoint, {
				method: "POST",
				headers: new Headers([
					["Content-Type", "application/json"],
				]),
				body: JSON.stringify(body),
			}).catch(networkError).then(checkStatus);
		},
		put(endpoint: string) {
			return fetch(endpoint, {
				method: "PUT",
			}).catch(networkError).then(checkStatus);
		},
		delete_(endpoint: string) {
			return fetch(endpoint, {
				method: "DELETE",
			}).catch(networkError).then(checkStatus);
		},
	}
}

export function createAPI(fetch: FetchAPIFunc): API {
	let unauthorizedHandlers: UnauthorizedHandler[] = [];
	
	const { get, post, put, delete_ } = createNetwork(fetch, unauthorizedHandlers);
	
	const api = {
		auth: {
			login: (email: string, password: string) =>
				post(authEndpoint("login"), { email, password })
					.then(bodyless),
				
			logout: () =>
				post(authEndpoint("logout")).then(bodyless),

			forgot: (email: string) =>
				post(authEndpoint("forgot"), { email }).then(bodyless),

			reset: (requestID: string, token: string, password: string) =>
				post(authEndpoint("reset"), {
					request_id: requestID,
					token,
					password,
				}).then(bodyless),
		},
		users: {
			me: {
					get: () =>
						get(endpoint("users", "@me")).then(json),

					organizations: () =>
						get(endpoint("users", "@me", "organizations")).then(json),

					invites: {
						get: () =>
							get(endpoint("users", "@me", "invites")).then(json),

						id: (inviteID: string) => ({
							get: () =>
								get(endpoint("users", "@me", "invites", inviteID)).then(json),
							
							post: () =>
								post(endpoint("users", "@me", "invites", inviteID)).then(bodyless),

							delete: () =>
								delete_(endpoint("users", "@me", "invites", inviteID)).then(bodyless),
						}),
					},
				},
			post: (
				requestID: string,
				token: string,
				givenName: string,
				middleNames: string,
				familyName: string,
				password: string,
			) =>
				post(endpoint("users"), {
					request_id: requestID,
					token,
					given_name: givenName,
					middle_names: middleNames,
					family_name: familyName,
					password,
				}).then(bodyless),
		},
		organizations: {
			id: (organizationID: string) => ({
				root: {
					createOrganization: (name: string) =>
						post(endpoint("organizations", organizationID, "root", "create-organization"), { name }).then(bodyless),

					addUserToOrganization: (userOrganizationID: string, email: string, role: OrganizationMemberRole) => 
						post(endpoint("organizations", organizationID, "root", "add-user-to-organization"), { organization_id: userOrganizationID, email, role }).then(bodyless),
				},
				alarmgroups: {
					get: () =>
						get(endpoint("organizations", organizationID, "alarmgroups")).then(json),
					
					id: (alarmGroupID: string) => ({
						assignable: () =>
							get(endpoint("organizations", organizationID, "alarmgroups", alarmGroupID, "assignable")).then(json),
						
						members: {
							id: (memberID: string) => ({
								put: () =>
									put(endpoint("organizations", organizationID, "alarmgroups", alarmGroupID, "members", memberID)).then(bodyless),
				
								delete: () =>
									delete_(endpoint("organizations", organizationID, "alarmgroups", alarmGroupID, "members", memberID)).then(bodyless),
							}),
						},
					}),
				},
				organizationusers: {
					get: () =>
						get(endpoint("organizations", organizationID, "users")).then(json)
				},
				invites: {
					get: () => get(endpoint("organizations", organizationID, "invites")).then(json),
					post: (email: string, givenName: string, middleNames: string, familyName: string) =>
						post(endpoint("organizations", organizationID, "invites"), {
							email,
							given_name: givenName,
							middle_names: middleNames,
							family_name: familyName
						}).then(bodyless),
					delete: (inviteID: string) => delete_(endpoint("organizations", organizationID, "invites", inviteID)).then(bodyless),
				},
			}),
		},
	};

	const onUnauthorized = (handler: UnauthorizedHandler) => {
		unauthorizedHandlers.push(handler);
	}

	return { ...api, onUnauthorized };
}

export const APIContext = React.createContext<API>(
	// We lie to typescript here and say that this empty object represents a
	// complete API. This is because we are forced to supply a default value
	// here, but this default value should _never_ be used. Instead the context
	// should only be consumed when it has been provided.
	{} as API
);

export function useAPI(): API {
	return useContext(APIContext);
}
